import { ConsultBanner, ServicesGrid, FeeSection, PlansSection, TermsSection } from "@/components/marketing/ServiceSections";

const SERVICES = [
  {
    title: "Medical Record Review & Case Analysis",
    price: "From $500",
    desc: "Professional RN review of medical records to help counsel understand the clinical story, identify significant medical events, and efficiently evaluate the medical aspects of a matter.",
    includes: ["Review & organization of supplied records", "Significant diagnoses, treatments & procedures", "Relevant provider identification", "Apparent record-gap identification", "Concise RN medical case summary", "Areas warranting further investigation or expert review"],
  },
  {
    title: "Medical Chronologies & Summaries",
    price: "From $750",
    desc: "Structured, date-by-date medical timelines with source references — designed for cases that demand an easy-to-follow clinical narrative.",
    includes: ["Date-by-date medical chronology", "Provider & facility identification", "Procedures, imaging & lab findings", "Medication history where relevant", "Hospitalizations & major medical events", "Source references to facilitate attorney review"],
  },
  {
    title: "Veteran & VA Case Medical Support",
    price: "From $1,250",
    desc: "Specialized RN medical-record analysis supporting VA-accredited attorneys and VA-accredited agents in matters involving Veterans.",
    includes: ["Military, VA & civilian record review", "Service-to-present medical timeline", "Documented diagnoses & symptom identification", "Cross-provider documentation comparison", "Medical evidence-gap identification", "RN medical evidence summary"],
  },
  {
    title: "Medical Research & Standards of Care",
    price: "$225–$250 / hour",
    desc: "Research tailored to the medical issues presented by the case — literature and standards-of-care analysis aligned to specialty and jurisdiction.",
    includes: ["Medical literature research ($225/hr)", "Standards-of-care research ($250/hr)", "Guideline & deviation mapping", "Source-referenced findings", "Bibliography of authorities", "Questions for counsel to consider"],
  },
  {
    title: "Litigation & Expert Support",
    price: "$225 / hour",
    desc: "Support for attorneys working with physicians, nurses, and other healthcare experts — deposition analysis and expert preparation.",
    includes: ["Deposition summary & testimony comparison", "Identification of testimony–record inconsistencies", "Expert review material organization", "Clinical question development", "Medical reference material preparation", "Expert/deposition preparation support"],
  },
  {
    title: "Secure Record Handling & Delivery",
    price: "Included in engagements",
    desc: "Encrypted document vault, record organization and indexing, and controlled exchange — built for PHI/PII-sensitive matters and large record sets.",
    includes: ["Encrypted upload & exchange", "Record organization & indexing", "Access-controlled case vault", "Download tracking & audit trail", "Retention & disposal controls", "Mandatory RN review & validation before delivery"],
  },
];

const FEE_TIERS = [
  { pages: "0–250", std: "$500", chrono: "$750", comp: "$1,000" },
  { pages: "251–500", std: "$750", chrono: "$1,100", comp: "$1,500" },
  { pages: "501–1,000", std: "$1,250", chrono: "$1,750", comp: "$2,400" },
  { pages: "1,001–1,500", std: "$1,750", chrono: "$2,500", comp: "$3,250" },
  { pages: "1,501–2,500", std: "$2,500", chrono: "$3,500", comp: "$4,500" },
  { pages: "2,501–4,000", std: "$3,500", chrono: "$4,750", comp: "$6,000" },
  { pages: "4,001–6,000", std: "$4,750", chrono: "$6,250", comp: "$7,750" },
  { pages: "6,000+", std: "Custom quote", chrono: "Custom quote", comp: "Custom quote" },
];

const PLANS = [
  { name: "Essential Counsel", price: "$995", capacity: "Up to 500 record pages / month", includes: ["Basic RN record review", "Record organization", "Concise medical summary", "1 RN consultation", "Secure case portal access", "Preferred scheduling"], testid: "plan-essential" },
  { name: "Professional Counsel", price: "$1,995", capacity: "Up to 1,250 pages / month", includes: ["Medical record review", "Medical chronologies", "Record-gap identification", "Medical summaries", "2 RN consultations", "Priority scheduling"], testid: "plan-professional" },
  { name: "Premier Litigation", price: "$3,495", capacity: "Up to 2,500 pages / month", includes: ["Comprehensive RN analysis", "Detailed chronologies", "Medical evidence & gap analysis", "Research allowance per engagement", "4 RN consultations", "Priority case queue"], testid: "plan-premier" },
  { name: "VA Advocacy Partner", price: "$2,495", capacity: "Up to 1,500 pages / month", includes: ["Veteran medical evidence review", "Military, VA & civilian record organization", "Medical timelines", "Evidence-gap identification", "RN medical summaries", "Secure case collaboration"], testid: "plan-va" },
  { name: "Firm / Government", price: "Custom", capacity: "Customized volume & workflow", includes: ["Multiple authorized users", "High-volume record analysis", "Dedicated workflows", "Enhanced security requirements", "Controlled information handling", "Customized reporting"], testid: "plan-firm" },
];

const HOURLY = [
  ["Additional RN consultation", "$200 / hour"],
  ["Medical literature research", "$225 / hour"],
  ["Standards-of-care research", "$250 / hour"],
  ["Deposition analysis", "$225 / hour"],
  ["Expert preparation / support", "$225 / hour"],
  ["Custom visual medical timeline", "From $500"],
];

const RUSH = [
  ["Priority — approx. 5 business days", "+25%"],
  ["Expedited — approx. 72 hours", "+40%"],
  ["Emergency — approx. 48 hours", "+50%"],
];

export default function Services() {
  return (
    <div className="fade-in-up">
      {/* HEADER */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="text-caption text-[#B91C1C] mb-4">SERVICES & FEE SCHEDULE</div>
          <h1 className="text-4xl lg:text-5xl font-serif font-bold text-[#0A192F] max-w-3xl leading-tight">Defined medical review deliverables — priced around scope and volume, not per page.</h1>
          <p className="text-slate-600 max-w-2xl mt-5 text-lg">Flat-rate ranges by record volume, recurring counsel memberships, and hourly professional services — every deliverable reviewed and signed by a licensed Registered Nurse.</p>
        </div>
      </section>
      <ConsultBanner />
      <ServicesGrid services={SERVICES} />
      <FeeSection feeTiers={FEE_TIERS} hourly={HOURLY} rush={RUSH} />
      <PlansSection plans={PLANS} />
      <TermsSection />
    </div>
  );
}
